import React, { useCallback, useState } from "react";

import { DeleteOutlined, PhoneFilled, SaveOutlined } from "@ant-design/icons";
import { Select, Tooltip } from "antd";
import { Formik, Field, FieldProps } from "formik";
import { MdOutlineChat } from "react-icons/md";

import PhoneLinkPlus from "../../../../assets/PhoneLinkPlus";
import { useClient } from "../../../../hooks/ClientProvider";
import api from "../../../../services/api";
import {
  notifyError,
  notifySucces,
  notifyWarning,
} from "../../../../utils/notifications";
import useCheckPermission from "../../../../utils/useCheckPermission";
import Can from "../../../Can";
import Loading from "../../../Loading";
import {
  addNewPhoneSchema,
  CELULAR_TYPE,
  getPhoneValues,
  phoneTypes,
} from "./schema";
import * as Style from "./style";
import { AddPhoneForm, CreatePhonePayload, PhoneType } from "./types";

const { Option } = Select;

type AddNewPhoneProps = {
  onAddNewPhone: (obsDetail: string) => Promise<void>;
  envioSmsHabilitado: boolean;
};

export function AddNewPhone({
  onAddNewPhone,
  envioSmsHabilitado,
}: AddNewPhoneProps) {
  const { client } = useClient();
  const clientId = client?.client?.id;

  const hasPermissionPhone = useCheckPermission("add_phone_number");

  const [isNewPhoneFormExpanded, setIsNewPhoneFormExpanded] =
    useState<boolean>(false);

  const handleAddPhones = useCallback(() => {
    if (isNewPhoneFormExpanded) {
      notifyError("Preencha o novo contato.");
    } else {
      setIsNewPhoneFormExpanded(true);
    }
  }, [isNewPhoneFormExpanded]);

  const handleRemovePhone = useCallback(() => {
    setIsNewPhoneFormExpanded(false);
  }, []);

  const handleSavePhone = useCallback(
    async (
      values: AddPhoneForm,
      setSubmitting: (isSubmitting: boolean) => void
    // eslint-disable-next-line consistent-return
    ) => {
      try {
        const { area, tel } = getPhoneValues(values.phone);

        const formattedPhoneValues: CreatePhonePayload = {
          faturaDigital: values.isRecebeFaturaSms,
          receberSMS: values.utilizadoServicoSms,
          area,
          telefone: tel,
          tipo: values.type,
          taxaBonificada: values.utilizadoTaxaBonificada ?? false,
        };

        const response = await api.post(
          `phones/create?client_id=${clientId}`,
          formattedPhoneValues
        );

        const hasCustomError =
          !!response?.statusCode &&
          typeof response.statusCode === "number" &&
          response?.statusCode?.toString()?.at(0) !== "2";

        if (hasCustomError && response?.type !== "warning") {
          return notifyError(response.message);
        }

        const obsDetail = [
          "Inclusão de telefone",
          `tipo: ${values.type}`,
          `número: (${area}) ${tel}`,
          values.utilizadoServicoSms && "recebimento SMS: sim",
          values.isRecebeFaturaSms && "fatura por SMS: sim",
          values.utilizadoTaxaBonificada && "taxa bonificada: sim",
        ]
          .filter(Boolean)
          .join(" | ");

        try {
          await onAddNewPhone(obsDetail);

          if (hasCustomError) {
            notifyWarning(response.message);
          } else {
            notifySucces(response.message);
          }
        } catch (err) {
          notifyError("Houve um erro ao carregar os telefones");
        }
      } catch (err) {
        const error = err as ErrorMessage;
        notifyError(error.message);
      } finally {
        setSubmitting(false);
        setIsNewPhoneFormExpanded(false);
      }
    },
    [isNewPhoneFormExpanded, client]
  );

  return (
    <>
      {isNewPhoneFormExpanded && (
        <Formik
          initialValues={{
            type: "DEFAULT",
            phone: "",
            utilizadoTaxaBonificada: false,
            utilizadoServicoSms: false,
            isRecebeFaturaSms: false,
          }}
          onSubmit={(values, { setSubmitting }) => {
            handleSavePhone(
              {
                phone: values.phone,
                type: values.type as PhoneType,
                isRecebeFaturaSms: values.isRecebeFaturaSms,
                utilizadoServicoSms: values.utilizadoServicoSms,
                utilizadoTaxaBonificada: values.utilizadoTaxaBonificada,
              },
              setSubmitting
            );
          }}
          onReset={() => {
            handleRemovePhone();
          }}
          validationSchema={addNewPhoneSchema}
        >
          {({
            handleSubmit,
            values,
            handleReset,
            setFieldValue,
            isSubmitting,
            setValues,
          }) => (
            <Style.FormWapper onSubmit={handleSubmit} onReset={handleReset}>
              <Style.InputsContent>
                <Style.InputsLabel>Tipo</Style.InputsLabel>
                <Field name="type">
                  {({ field, meta }: FieldProps<string>) => (
                    <Style.InputsRow
                      className={`${meta.touched && meta.error ? "error" : ""}`}
                    >
                      <Style.CustomSelect
                        {...field}
                        disabled={isSubmitting}
                        onChange={(value) => {
                          setFieldValue("type", value);
                          setValues({
                            isRecebeFaturaSms: false,
                            utilizadoServicoSms: false,
                            utilizadoTaxaBonificada: false,
                            type: value as string,
                            phone: values.phone,
                          });
                        }}
                      >
                        <Option value="DEFAULT">Selecione o tipo</Option>
                        {phoneTypes.map((phoneType) => (
                          <Option
                            value={phoneType}
                            key={`add-phone-${phoneType}`}
                          >
                            {phoneType}
                          </Option>
                        ))}
                      </Style.CustomSelect>
                    </Style.InputsRow>
                  )}
                </Field>
                <Style.CustomErrorMessage name="type" component="div" />
              </Style.InputsContent>

              <Style.InputsContent>
                <Style.InputsLabel>Número</Style.InputsLabel>
                <Field name="phone">
                  {({ field, meta }: FieldProps<string>) => (
                    <Style.InputsRow
                      className={`${meta.touched && meta.error ? "error" : ""}`}
                    >
                      <Style.CustomReactInputMask
                        {...field}
                        value={field.value}
                        disabled={isSubmitting}
                        onChange={field.onChange}
                        mask={
                          values.type === "CELULAR" ||
                          values.type === "REFERENCIA" ||
                          values.type === "COMERCIAL"
                            ? "(99) 99999-9999"
                            : "(99) 9999-9999"
                        }
                        maskChar={null}
                      />
                    </Style.InputsRow>
                  )}
                </Field>
                <Style.CustomErrorMessage name="phone" component="div" />
              </Style.InputsContent>

              <Style.TelephoneTypeContent>
                {isSubmitting ? (
                  <Loading />
                ) : (
                  <Style.TelephoneTrash>
                    <button type="submit" title="Salvar">
                      <SaveOutlined />
                    </button>
                    {values.type === CELULAR_TYPE && (
                      <>
                        <Can permission="view_number_subsidized">
                          <Field name="utilizadoTaxaBonificada">
                            {({ field }: FieldProps<boolean>) => (
                              <>
                                <Tooltip
                                  title={
                                    field.value
                                      ? "Número Bonificado"
                                      : "Ativar Número bonificado"
                                  }
                                >
                                  <button
                                    type="button"
                                    className={field.value ? "on" : "off"}
                                    onClick={() => {
                                      setFieldValue(
                                        "utilizadoTaxaBonificada",
                                        !field.value
                                      );
                                    }}
                                  >
                                    <PhoneLinkPlus />
                                  </button>
                                </Tooltip>
                              </>
                            )}
                          </Field>
                        </Can>
                        <Can permission="main_contact">
                          <Field name="utilizadoServicoSms">
                            {({ field }: FieldProps<boolean>) => (
                              <Tooltip
                                title={
                                  field.value
                                    ? "Número ativo"
                                    : "Ativar Recebimento de sms"
                                }
                              >
                                <button
                                  type="button"
                                  className={field.value ? "on" : "off"}
                                  onClick={() => {
                                    setFieldValue(
                                      "utilizadoServicoSms",
                                      !field.value
                                    );
                                  }}
                                >
                                  <PhoneFilled />
                                </button>
                              </Tooltip>
                            )}
                          </Field>
                        </Can>
                        {envioSmsHabilitado && (
                          <Can permission="add_or_cancel_invoice_by_Sms">
                            <Field name="isRecebeFaturaSms">
                              {({ field }: FieldProps<boolean>) => (
                                <Tooltip
                                  title={
                                    field.value
                                      ? "Habilitado fatura por SMS"
                                      : "Ativar fatura por SMS"
                                  }
                                >
                                  <button
                                    type="button"
                                    className={field.value ? "on" : "off"}
                                    onClick={() => {
                                      setFieldValue(
                                        "isRecebeFaturaSms",
                                        !field.value
                                      );
                                    }}
                                  >
                                    <MdOutlineChat />
                                  </button>
                                </Tooltip>
                              )}
                            </Field>
                          </Can>
                        )}
                      </>
                    )}
                    <button type="reset" title="Deletar">
                      <DeleteOutlined />
                    </button>
                  </Style.TelephoneTrash>
                )}
              </Style.TelephoneTypeContent>
            </Style.FormWapper>
          )}
        </Formik>
      )}
      <Style.BtnPhone>
        <Style.AddButton
          type="button"
          onClick={() => {
            if (hasPermissionPhone) {
              handleAddPhones();
            }
          }}
        >
          {hasPermissionPhone
            ? "Adicionar contato"
            : "Usuário sem permissão para adicionar telefones"}
        </Style.AddButton>

        <div className="bg-btn" />
      </Style.BtnPhone>
    </>
  );
}